import styled from "styled-components";
import { useState, useRef, useEffect } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ReactPlayer from "react-player";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { BANNERS } from "constants/banners";

import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import Dropdown from "components/forms/Dropdown";
import DropdownSelection from "components/forms/DropdownSelection";
import SelectionItem from "components/forms/SelectionItem";
import InputWithLabel from "components/forms/InputWithLabel";
import InputText from "components/forms/InputText";

import copyIcon from "assets/img/icons/copy-icon.svg";
import copyWhiteIcon from "assets/img/icons/copy-white-icon.svg";
import checkIcon from "assets/img/icons/icons8-check.svg";
import checkWhiteIcon from "assets/img/icons/icons8-check-white.svg";

const MarketingToolsBox = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BoxTitle = styled.div`
  width: 100%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};

  display: flex;
  align-items: center;
  gap: 8px;
`;

const Text = styled.div`
  line-height: 125%;
`;

const DropdownWrap = styled.div`
  position: relative;
  width: 100%;
  max-width: 290px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BannerSizeSelectionItem = styled.div``;

const Banners = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const BannersTypeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
`;

const BannersTypeSectionTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const Banner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const BannerImageBox = styled.div`
  max-width: ${(props) => (props.maxwidth ? props.maxwidth : "100%")};
`;

const InputWrap = styled.div`
  position: relative;
  width: 100%;
`;

const CopyIconBox = styled.div`
  position: absolute;
  top: 50%;
  right: 15px;

  transform: translateY(-50%);
`;

export default ({ data }) => {
  const bannerSizeDropdownRef = useRef();
  const bannerSizeSelectionRef = useRef();

  const [bannerSizeSelectionOpened, setBannerSizeSelectionOpened] =
    useState(false);
  const [bannerSize, setBannerSize] = useState(null);
  const [copied, setCopied] = useState(false);

  function useBannerSizeSelectionOutsideClick(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !bannerSizeDropdownRef.current.contains(event.target)
        ) {
          toggleBannerSizeSelection();
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  useBannerSizeSelectionOutsideClick(bannerSizeSelectionRef);

  const toggleBannerSizeSelection = () => {
    setBannerSizeSelectionOpened((prevState) => !prevState);
  };

  const onBannerSizeSelect = (banner) => {
    setBannerSize(banner);
    setBannerSizeSelectionOpened(false);
  };

  const linkCopied = () => {
    setCopied(true);

    setTimeout(() => setCopied(false), 2000);
  };

  const bannerSizeOptions = [
    "300x250",
    "336x280",
    "728x90",
    "468x60",
    "160x600",
    "120x600",
    "320x100",
    "320x50",
    "200x200",
    "125x125",
  ];

  const bannerSizeOptionsList = bannerSizeOptions.map((option) => (
    <SelectionItem onClick={() => onBannerSizeSelect(option)}>
      {option} px
    </SelectionItem>
  ));

  // console.log("bannerSizeOptionsList", bannerSizeOptionsList);
  // console.log("bannerSize", bannerSize);

  let gifBanners = [];

  if (BANNERS[bannerSize]?.gif) {
    gifBanners = BANNERS[bannerSize].gif.map((banner) => (
      <Banner>
        <BannerImageBox>
          <img src={banner} alt="Scalelup" style={{ maxWidth: "100%" }} />
        </BannerImageBox>
        <InputWrap>
          <InputText type="text" value={banner} readOnly={true} />
          <CopyIconBox>
            <CopyToClipboard text={banner} onCopy={linkCopied}>
              <ButtonRoundIcon
                icon={copied ? checkIcon : copyIcon}
                hoverIcon={copied ? checkWhiteIcon : copyWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                buttonSize="24px"
                iconSize="10px"
              />
            </CopyToClipboard>
          </CopyIconBox>
        </InputWrap>
      </Banner>
    ));
  }

  let html5Banners = [];

  if (BANNERS[bannerSize]?.html5) {
    html5Banners = BANNERS[bannerSize].html5.map((banner) => (
      <Banner>
        <BannerImageBox maxwidth={`${bannerSize.split("x")[0]}px`}>
          <ReactPlayer
            url={`https://gw-creatives.s3.eu-west-1.amazonaws.com/banners/html5-v1/previews/scalelup_${bannerSize}_v1.mp4`}
            controls={false}
            playing={true}
            loop={true}
            width="100%"
            height="100%"
          />
        </BannerImageBox>
        <InputWrap>
          <InputText type="text" value={banner} readOnly={true} />
          <CopyIconBox>
            <CopyToClipboard text={banner} onCopy={linkCopied}>
              <ButtonRoundIcon
                icon={copied ? checkIcon : copyIcon}
                hoverIcon={copied ? checkWhiteIcon : copyWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                buttonSize="24px"
                iconSize="10px"
              />
            </CopyToClipboard>
          </CopyIconBox>
        </InputWrap>
      </Banner>
    ));
  }

  return (
    <MarketingToolsBox>
      <BoxTitle>Scalelup Web Banners</BoxTitle>
      <Text>
        Place a banner on your website, bring your visitors to us, and gain more
        referrals.
      </Text>
      <InputWithLabel
        label="Dimensions"
        width="100%"
        marginTop={css.VERTICAL_BOX_MARGIN_MEDIUM}
      >
        <DropdownWrap>
          <Dropdown
            onClick={toggleBannerSizeSelection}
            dropdownRef={bannerSizeDropdownRef}
          >
            {!bannerSize && "Select banner size"}
            {bannerSize && (
              <BannerSizeSelectionItem>{bannerSize} px</BannerSizeSelectionItem>
            )}
          </Dropdown>
          {bannerSizeSelectionOpened && (
            <DropdownSelection
              width="150px"
              top={bannerSize ? "48px" : "-306px"}
              selectionRef={bannerSizeSelectionRef}
            >
              {bannerSizeOptionsList}
            </DropdownSelection>
          )}
        </DropdownWrap>
      </InputWithLabel>
      <Banners>
        {gifBanners.length > 0 && (
          <BannersTypeSection>
            <BannersTypeSectionTitle>
              {bannerSize} px GIF Banners
            </BannersTypeSectionTitle>
            {gifBanners}
          </BannersTypeSection>
        )}
        {html5Banners.length > 0 && (
          <BannersTypeSection>
            <BannersTypeSectionTitle>
              {bannerSize} px HTML5 Banners
            </BannersTypeSectionTitle>
            {html5Banners}
          </BannersTypeSection>
        )}
      </Banners>
    </MarketingToolsBox>
  );
};
